import React from 'react';
import OptimizedImage from './OptimizedImage';
import { VerifiedBadgeIcon, TickIcon } from '../custom-widgets/IconAssets';
import styles from './PlayerHero.module.css';

const PlayerHero = ({
    player,
    clubName,
    clubLogo,
    playerPosition,
    statscoreData,
    positionStats
}) => {
    const playerFallbackSrc = '/player/fallback.png';
    const venueImage = player.venue_image;
    const venueName = player.venue_name;
    const established = player.club_established;
    const clubDisplayName = player.club_full_name || player.club_display_name || clubName || player.club;
    const areaName = player.club_area || player.area_name;

    const rawName = player.display_name || player.name || '';
    const bgText = rawName.toUpperCase();
    const charCount = bgText.length;
    const bgNameSize = `${Math.max(6, Math.min(11, Math.floor(200 / charCount)))}vw`;
    const bgNameSizeMobile = `${Math.max(8, Math.min(22, parseFloat((216 / charCount).toFixed(1))))}vw`;
    const bgLeft = charCount > 18 ? '55%' : charCount > 14 ? '60%' : charCount > 10 ? '65%' : '75%';

    return (
        <header className={styles['hero-header']}>
            <div className={styles['hero-content']}>
                <div className={styles['forensic-status-badge']}>
                    <VerifiedBadgeIcon width={22} height={22} />
                    <span>Forensic Verified</span>
                </div>

                <div className={styles['player-name-container']}>
                    <h1 className={styles['player-name-full']}>
                        {player.name}
                    </h1>
                    <span className={styles['checkmark']}>
                        <TickIcon />
                    </span>
                </div>

                <div className={styles['meta-row']}>
                    {clubDisplayName && (
                        <div className={styles['meta-item']}>
                            {(player.club_image || clubLogo) && (
                                <OptimizedImage
                                    src={player.club_image || clubLogo}
                                    alt=""
                                    width={28}
                                    height={28}
                                    unoptimized
                                    style={{ marginRight: '10px', borderRadius: '50%' }}
                                />
                            )}
                            <span>{clubDisplayName}</span>
                        </div>
                    )}
                    {areaName && (
                        <div className={styles['meta-item']}>
                            <span>{areaName}</span>
                        </div>
                    )}
                    {venueName && (
                        <div className={styles['meta-item']}>
                            <span>🏟️ {venueName}</span>
                        </div>
                    )}
                    {established && (
                        <div className={styles['meta-item']}>
                            <span>Est. {established}</span>
                        </div>
                    )}
                    {player.age && (
                        <div className={styles['meta-item']}>
                            <span>{String(player.age).split(' ')[0]}</span>
                        </div>
                    )}
                    {player.position && (
                        <div className={styles['meta-item']}>
                            <span>{player.position}</span>
                        </div>
                    )}
                    {player.foot && (
                        <div className={styles['meta-item']}>
                            <span>{player.foot}</span>
                        </div>
                    )}
                </div>

                <div className={styles['volume-strip']}>
                    {[
                        { label: 'Appearances', labelMobile: 'APPS', value: positionStats?.appearances || '0' },
                        { label: 'Starts', labelMobile: 'STT', value: positionStats?.starts || '0' },
                        { label: 'Minutes Played', labelMobile: 'M.PLD', value: positionStats?.minutesPlayed || '0' },
                        { label: 'Goals', labelMobile: 'GLS', value: positionStats?.goals || '0' },
                        { label: 'Assists', labelMobile: 'ASTS', value: positionStats?.assists || '0' },
                    ].map((item, index) => (
                        <div key={`vol-${index}`} className={styles['volume-item']}>
                            <span className={styles['volume-label']}>
                                <span className={styles['desktop-label']}>{item.label}</span>
                                <span className={styles['mobile-label']}>{item.labelMobile}</span>
                            </span>
                            <span className={styles['volume-value']}>{item.value}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className={styles['hero-bg-container']}>
                {venueImage && (
                    <div className={styles['venue-background']}>
                        <OptimizedImage
                            src={venueImage}
                            alt="Venue"
                            layout="fill"
                            objectFit="cover"
                            unoptimized
                            className={styles['venue-image']}
                        />
                        <div className={styles['venue-overlay']} />
                    </div>
                )}
                <div className={styles['hero-particles']} />
                <div className={styles['hero-gold-bar']} />
                {bgText && (
                    <div
                        className={styles['player-surname-bg']}
                        style={{
                            '--bg-name-size': bgNameSize,
                            '--bg-name-size-mobile': bgNameSizeMobile,
                            '--bg-name-left': bgLeft
                        }}
                    >
                        {bgText}
                    </div>
                )}
            </div>

            <div className={styles['player-action-container']}>
                <OptimizedImage
                    src={
                        (Array.isArray(player.photo) && player.photo.length > 0)
                            ? player.photo[0].url
                            : player.photo
                    }
                    fallbackSrc={"/player/fallback.png"}
                    alt={player.name || ""}
                    width={1000}
                    height={1200}
                    unoptimized
                    className={styles['player-action-shot']}
                />
            </div>
        </header>
    );
};

export default PlayerHero;
